import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { LayoutDashboard, LayoutTemplate, LogOut } from "lucide-react";
import { useAuthStore } from "../../stores/auth";
import { ZirroLogo } from "../ui/ZirroLogo";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * App shell with a sticky Zirro-branded header and primary navigation.
 * Signed-in people see their dashboard link, avatar and Log out; visitors
 * browsing templates or trying the create wizard see Log in and Sign up.
 * Wraps dashboard, template gallery, and onboarding pages.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const user = useAuthStore((s) => s.user);
  const signedIn = useAuthStore((s) => s.status === "authenticated");
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();
  // Return here after logging in or signing up from the header.
  const here = { from: `${location.pathname}${location.search}` };

  const navLinks = signedIn
    ? [
        { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/app/templates", label: "Templates", icon: LayoutTemplate },
      ]
    : [{ to: "/templates", label: "Templates", icon: LayoutTemplate }];

  const isActive = (path: string) =>
    location.pathname.startsWith(path) ||
    (path === "/app/templates" && location.pathname.startsWith("/templates"));
  const initial = user?.email ? user.email.charAt(0).toUpperCase() : "W";

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <div className="app-header__left">
            <Link className="app-header__logo" to={signedIn ? "/app/dashboard" : "/"} aria-label="Zirro Sites home">
              <ZirroLogo product="Sites" />
            </Link>

            <nav className="app-header__nav" aria-label="Main navigation">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const active = isActive(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    aria-label={link.label}
                    aria-current={active ? "page" : undefined}
                    className={`app-header__nav-link ${active ? "app-header__nav-link--active" : ""}`}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="app-header__right">
            {signedIn ? (
              <>
                <div className="app-avatar" title={user?.email ?? "Workspace"} aria-label="Workspace">
                  {initial}
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={async () => {
                    await logout();
                    navigate("/", { replace: true });
                  }}
                >
                  <LogOut size={14} aria-hidden="true" />
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link className="btn btn--ghost btn--sm" to="/login" state={here}>
                  Log in
                </Link>
                <Link className="btn btn--primary btn--sm" to="/signup" state={here}>
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {children}
    </div>
  );
}
